<template>
  <div class="mt-2">
    <div class="bg-white p-3 rounded-lg shadow-sm" v-if="canViewResults">
      <h2 class="text-base font-bold mb-3 text-gray-800">View Results</h2>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 mb-3" v-if="!isTertiary">
        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Classroom</label>
          <ClassroomSectionSelect v-model="selectedClassroom" className="w-full" :showClassroomsOnly="false" />
        </div>

        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">{{ labels.TERM }}</label>
          <AcademicCalendarSelect v-model="selectedTerm" size="small" :placeholder="`Select ${labels.TERM}`" className="w-full" />
        </div>

        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Status</label>
          <EnrollmentStatusSelect v-model="selectedStatus" size="small" @change="onFilterChange" />
        </div>

        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Student</label>
          <Select
            v-model="selectedStudent"
            :options="studentOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Student"
            size="small"
            class="w-full rounded-lg"
            filter
            showClear
            :loading="loadingStudents"
            @filter="onStudentFilter"
          />
        </div>
      </div>

      <!-- Tertiary Filters -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2 mb-3" v-else>
        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Programme</label>
          <ProgrammeSelect v-model="selectedProgramme" @change="onFilterChange" />
        </div>

        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Level</label>
          <ClassroomSectionSelect v-model="selectedLevel" placeholder="Select Level" :showClassroomsOnly="false" @change="onFilterChange" />
        </div>

        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">{{ labels.TERM }}</label>
          <AcademicCalendarSelect v-model="selectedTerm" size="small" :placeholder="`Select ${labels.TERM}`" className="w-full" />
        </div>

        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Status</label>
          <EnrollmentStatusSelect v-model="selectedStatus" size="small" @change="onFilterChange" />
        </div>

        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Student</label>
          <Select v-model="selectedStudent" :options="studentOptions" optionLabel="label" optionValue="value"
            placeholder="Search by name or matric" size="small" class="w-full rounded-lg" filter showClear :loading="loadingStudents" :disabled="!selectedProgramme" @filter="onStudentFilter" />
        </div>
      </div>

      <!-- Class View: Student List -->
      <div v-if="showStudentsTable" class="mt-3">
        <div class="flex items-center justify-between mb-2 gap-3">
          <h3 class="text-sm font-semibold shrink-0">Class List</h3>
          <div class="flex-1 max-w-xs">
            <InputText v-model="classListSearch" size="small" placeholder="Search by name or matric no..."
              class="w-full rounded-lg" @input="onClassListSearch" />
          </div>
          <div v-if="selectedTerm" class="flex gap-1.5">
            <Button
              label="Publish All"
              icon="pi pi-check-circle"
              severity="primary"
              size="small"
              :loading="bulkPublishing"
              :disabled="!studentsList.length"
              @click="bulkPublishResults"
              data-testid="btn-results-bulk-publish"
            />
            <Button
              label="Unpublish All"
              icon="pi pi-times-circle"
              severity="danger"
              size="small"
              outlined
              :loading="bulkUnPublishing"
              :disabled="!studentsList.length"
              @click="bulkUnPublishResults"
              data-testid="btn-results-bulk-unpublish"
            />
          </div>
        </div>
        <DataTable   :value="studentsList" lazy paginator
          :rows="rows"
          :totalRecords="totalRecords"
          :loading="loadingStudents"
          :rowsPerPageOptions="[5, 10, 20, 50]"
          @page="onPageChange"
          stripedRows
          size="small"
          class="p-datatable-sm">
          <Column field="name" header="Name"></Column>
          <Column field="matric_no" header="Admission No"></Column>
          <Column field="gender" header="Gender"></Column>
          <Column header="Actions">
            <template #body="slotProps">
              <Button icon="pi pi-eye" label="View Result" class="p-button-sm p-button-outlined"
                @click="selectedStudent = slotProps.data.id" data-testid="btn-results-view-student" />
            </template>
          </Column>
        </DataTable>
        <div v-if="!studentsList.length && !loadingStudents" class="text-center text-gray-500 py-3 text-sm">
          No students found in this class.
        </div>

        <!-- Per-student failures from the last bulk publish (partial success). -->
        <Panel
          v-if="bulkPublishFailures.length"
          toggleable
          class="mt-3"
          data-testid="bulk-publish-failures"
        >
          <template #header>
            <div class="flex items-center gap-2 text-amber-700">
              <i class="pi pi-exclamation-triangle" />
              <span class="text-sm font-semibold">
                {{ bulkPublishFailures.length }}
                {{ bulkPublishFailures.length === 1 ? 'student' : 'students' }} could not be published
              </span>
            </div>
          </template>
          <ul class="divide-y divide-gray-100">
            <li
              v-for="failure in bulkPublishFailures"
              :key="failure.student_id"
              class="py-2 text-sm"
              data-testid="bulk-publish-failure-row"
            >
              <span class="font-medium text-gray-800">{{ failure.student_name }}</span>
              <span class="text-gray-500"> — {{ failure.reason }}</span>
            </li>
          </ul>
        </Panel>
      </div>

      <div class="mt-4 overflow-hidden" v-if="selectedStudent">

        <div class="flex flex-col md:flex-row justify-between items-start mb-3">
          <Button label="Back to Class List" icon="pi pi-arrow-left" text @click="selectedStudent = null"
            class="!px-0" data-testid="btn-results-view-back" />
          <div class="flex gap-2">
            <button v-if="studentResult?.status !== ResultStatus.Published" @click="publishResult"
              :disabled="publishing || !selectedStudent || loadingResult"
              data-testid="btn-results-publish"
              class="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors shadow-sm disabled:opacity-50 font-medium text-xs flex items-center gap-1.5">
              <i class="pi pi-check-circle" v-if="!publishing && !loadingResult"></i>
              <i class="pi pi-spinner pi-spin" v-else></i>
              {{ publishing ? 'Publishing...' : (loadingResult ? 'Loading...' : 'Publish Result') }}
            </button>
            <button v-if="studentResult?.status === ResultStatus.Published" @click="unPublishResult"
              :disabled="unPublishing || !selectedStudent || loadingResult"
              data-testid="btn-results-unpublish"
              class="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shadow-sm disabled:opacity-50 font-medium text-xs flex items-center gap-1.5">
              <i class="pi pi-times-circle" v-if="!unPublishing && !loadingResult"></i>
              <i class="pi pi-spinner pi-spin" v-else></i>
              {{ unPublishing ? 'Unpublishing...' : (loadingResult ? 'Loading...' : 'Unpublish') }}
            </button>
            <button type="button" @click="downloadPdf" :disabled="loadingResult || downloading"
              data-testid="btn-results-download-top"
              class="px-4 py-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg transition-colors font-medium text-xs flex items-center gap-1.5 disabled:opacity-50">
              <i class="pi pi-spinner pi-spin" v-if="downloading"></i>
              <i class="pi pi-download" v-else></i>
              {{ downloading ? 'Downloading...' : 'Download PDF' }}
            </button>
          </div>
        </div>

        <template v-if="isTertiary && selectedStudent">
          <StudentResultTertiary
            :studentId="selectedStudent"
            :academicCalendarId="selectedTerm"

          />
        </template>

        <article v-else-if="selectedStudent" class="print:!p-0">
           <!-- Quick Test Chart Column Selector -->
           <div v-if="styleCode === 'detailed'" class="mb-2 flex items-center gap-2 bg-indigo-50/50 p-2.5 rounded-xl border border-indigo-100 shadow-sm print:hidden">
              <label class="text-[10px] font-black text-indigo-900 flex items-center gap-1.5 uppercase tracking-widest">
                <i class="pi pi-chart-line text-xs"></i>
                Chart Data:
              </label>
              <Select v-model="localChartColumn" :options="chartColumnOptions" optionLabel="label" optionValue="value" placeholder="Switch Chart Column" class="w-40" />

              <label class="text-[10px] font-black text-indigo-900 flex items-center gap-1.5 uppercase tracking-widest ml-2">
                <i class="pi pi-cog text-xs"></i>
                Type:
              </label>
              <Select v-model="localChartType" :options="chartTypeOptions" optionLabel="label" optionValue="value" class="w-28" />

              <p class="text-[10px] text-gray-400 font-bold uppercase tracking-tight ml-auto whitespace-nowrap hidden lg:block">Temporarily test different visualizations.</p>

              <Button
                v-if="localChartColumn || localChartType"
                icon="pi pi-save"
                label="Save Defaults"
                size="small"
                :loading="updatingTemplate"
                @click="saveChartDefaults(localChartColumn, localChartType)"
                data-testid="btn-results-save-chart-defaults"
                class="ml-auto lg:ml-2 !text-xs font-bold whitespace-nowrap"
                severity="primary" outlined
              />
           </div>

           <component
            :is="activeTemplate"
            :school="school!"
            :studentName="studentName"
            :studentClass="studentClass"
            :leftColumnInfo="leftColumnInfo"
            :rightColumnInfo="rightColumnInfo"
            :tableHeaders="tableHeaders"
            :subjects="subjects"
            :resultSummary="resultSummary"
            :grades="grades"
            :categorizedFeedbacks="categorizedFeedbacks"
            :nextTermDate="nextTermDate"
            :chartData="chartData"
            :chartType="localChartType"
            @save-feedback="saveFeedback"
          />
        </article>

        <!-- Post-publish correction trail (#227) — read-only, permission-gated. -->
        <Panel
          v-if="!isTertiary && canViewAmendments"
          toggleable
          class="mt-4 print:hidden"
          data-testid="result-amendment-history"
        >
          <template #header>
            <div class="flex items-center gap-2">
              <i class="pi pi-history text-primary"></i>
              <span class="text-sm font-semibold">Amendment history</span>
              <span v-if="amendments.length" class="text-xs text-muted-color">
                ({{ amendments.length }})
              </span>
            </div>
          </template>
          <ResultAmendmentTimeline :amendments="amendments" :loading="loadingAmendments" />
        </Panel>

        <div class="h-12"></div> <!-- Spacer -->
      </div>

      <div v-if="!selectedClassroom && !(isTertiary && selectedProgramme)" class="mt-4 text-center text-gray-500 text-sm">
        Please select a classroom or programme to view results.
      </div>

      <div class="flex justify-center items-center mt-3 space-x-3" v-if="selectedStudent">
        <button type="button" @click="downloadPdf" :disabled="downloading" class="w-36 bg-primary py-2.5 text-white rounded-md whitespace-nowrap text-center disabled:opacity-50 text-sm" data-testid="btn-results-download-bottom">
          {{ downloading ? 'Downloading...' : 'Download PDF' }}
        </button>
        <button v-if="studentResult?.status !== ResultStatus.Published" @click="publishResult"
          :disabled="publishing || !selectedStudent"
          data-testid="btn-results-publish-bottom"
          class="w-36 bg-primary py-2.5 text-white rounded-md whitespace-nowrap text-center disabled:opacity-50 text-sm">
          {{ publishing ? 'Publishing...' : 'Publish Result' }}
        </button>
        <button v-if="studentResult?.status === ResultStatus.Published" @click="unPublishResult"
          :disabled="unPublishing || !selectedStudent"
          data-testid="btn-results-unpublish-bottom"
          class="w-36 bg-red-600 py-2.5 text-white rounded-md whitespace-nowrap text-center disabled:opacity-50 text-sm">
          {{ unPublishing ? 'Unpublishing...' : 'Unpublish Result' }}
        </button>
      </div>
    </div>
    <div v-else class="bg-white p-3 rounded-lg shadow-sm text-center">
        <div class="flex flex-col items-center justify-center py-6">
            <div class="bg-red-50 p-3 rounded-full mb-3">
                <i class="pi pi-lock text-red-500 text-lg"></i>
            </div>
            <h3 class="text-base font-bold text-gray-900 mb-1.5">Access Denied</h3>
            <p class="text-gray-500 max-w-md text-sm">You do not have permission to view results. Please contact your administrator if you believe this is an error.</p>
        </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ClassroomSectionSelect from '@/components/common/ClassroomSectionSelect.vue';
import StudentSearchInput from '@/components/common/StudentSearchInput.vue';
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import { useViewResults } from './useViewResults';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useSchoolLabels } from '@/composables/useSchoolLabels';

const { labels } = useSchoolLabels();
import StudentResultTertiary from '@/components/DashBoard/Results/ViewResults/StudentResultTertiary.vue';
import ResultAmendmentTimeline from '@/components/DashBoard/Results/ViewResults/ResultAmendmentTimeline.vue';
import { useResultAmendmentHistory } from '@/composables/useResultAmendmentHistory';
import { useUserStore } from '@/store/userStore';
import { storeToRefs } from 'pinia';
import { can, AppPermissions } from '@/utils/permissions';

import ProgrammeSelect from '@/components/common/ProgrammeSelect.vue';
import ClassicTemplate from './Templates/ClassicTemplate.vue';
import ModernTemplate from './Templates/ModernTemplate.vue';
import DetailedTemplate from './Templates/DetailedTemplate.vue';

const { isTertiary } = useSchoolFeatures();
const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);

const canViewResults = computed(() => can(currentUser.value, AppPermissions.Results.Actions.View, AppPermissions.Results.Resource));

const {
  school,
  selectedTerm,
  selectedClassroom,
  selectedStudent,
  loadingStudents,
  studentsList,
  studentOptions,
  studentResult,
  ResultStatus,
  grades,
  tableHeaders,
  subjects,
  studentName,
  studentClass,
  leftColumnInfo,
  rightColumnInfo,
  teacherComment,
  principalComment,
  nextTermDate,
  publishing,
  unPublishing,
  publishResult,
  unPublishResult,
  loadingResult,
  feedbacks,
  selectedLevel,
  selectedStatus,
  saveFeedback,
  resultSummary,
  onFilterChange,
  selectedProgramme,
  onStudentFilter,
  classListSearch,
  onClassListSearch,
  rows,
  totalRecords,
  onPageChange,
  showStudentsTable,
  categorizedFeedbacks,
  styleCode,
  chartData,
  localChartColumn,
  localChartType,
  chartColumnOptions,
  saveChartDefaults,
  updatingTemplate,
  downloadPdf,
  downloading,
  bulkPublishResults,
  bulkUnPublishResults,
  bulkPublishing,
  bulkUnPublishing,
  bulkPublishFailures,
} = useViewResults();

// Post-publish score corrections for the selected student + term (#227).
const { canViewAmendments, amendments, loadingAmendments } = useResultAmendmentHistory(
  selectedStudent,
  selectedTerm,
);

const chartTypeOptions = [
  { label: 'Radar', value: 'radar' },
  { label: 'Bar', value: 'bar' },
  { label: 'Line', value: 'line' },
];

const activeTemplate = computed(() => {
  switch (styleCode.value) {
  case 'modern': return ModernTemplate;
  case 'detailed': return DetailedTemplate;
  case 'classic':
  default: return ClassicTemplate;
  }
});

</script>
